import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FiSquare, FiSun, FiMoon, FiPlay, FiPause, FiPhoneOff, FiChevronDown, FiCheck, FiVolume2 } from 'react-icons/fi';
import './Chat.css';
import api from '../../services/api';
import { stripMarkdown } from '../../utils/stripMarkdown';
import RichText from '../../components/common/RichText';
import { useMicVAD, utils } from "@ricky0123/vad-react"
import { useNavigate } from 'react-router-dom';
import voiceConfig from '../../config/deepgram_aura_v1_config.json';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  isUser: boolean;
  timestamp: Date;
  audio?: {
    audioUrl: string;
    duration: number;
  };
}

interface VoiceOption {
  id: string;
  name: string;
  gender: string;
  accent: string;
}

const Vad: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isResponding, setIsResponding] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [playingAudioUrl, setPlayingAudioUrl] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [selectedVoice, setSelectedVoice] = useState<string>(() => {
    const saved = localStorage.getItem('selectedVoice');
    return saved || 'aura-luna-en';
  });
  const [isVoiceMenuOpen, setIsVoiceMenuOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const botAudioRef = useRef<HTMLAudioElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const abortControllerRef = useRef<AbortController | null>(null);

  const availableVoices = useMemo<VoiceOption[]>(() => {
    const config = voiceConfig.deepgram_aura_v1;
    const femaleVoices = Object.entries(config.voices.female).map(([id, voice]) => ({
      id,
      name: voice.name,
      gender: 'female',
      accent: voice.accent,
    }));
    const maleVoices = Object.entries(config.voices.male).map(([id, voice]) => ({
      id,
      name: voice.name,
      gender: 'male',
      accent: voice.accent,
    }));
    return [...femaleVoices, ...maleVoices];
  }, []);

  const currentVoice = useMemo(
    () => availableVoices.find((voice) => voice.id === selectedVoice) || availableVoices[0],
    [availableVoices, selectedVoice],
  );

  const groupedVoices = useMemo(
    () =>
      ['female', 'male'].map((gender) => ({
        gender,
        voices: availableVoices.filter((voice) => voice.gender === gender),
      })),
    [availableVoices],
  );

  // VAD hook
  const vad = useMicVAD({
    model: "v5",
    baseAssetPath: "/",
    onnxWASMBasePath: "/",
    onSpeechEnd: (audio) => {
      console.log('VAD: Speech detected, processing audio...');
      // audio is a Float32Array
      const wavBuffer = utils.encodeWAV(audio);
      const base64 = utils.arrayBufferToBase64(wavBuffer);
      const url = `data:audio/wav;base64,${base64}`;
      // Show user audio message immediately
      setMessages((prev) => [
        ...prev,
        {
          role: 'user',
          content: '',
          isUser: true,
          timestamp: new Date(),
          audio: { audioUrl: url, duration: 0 }, // duration not available here
        },
      ]);
      // Send to backend
      const audioBlob = new Blob([wavBuffer], { type: 'audio/wav' });
      const audioFile = new File([audioBlob], 'audio.wav', { type: 'audio/wav' });
      handleSendAudioMessage(audioBlob, audioFile, 0);
    },
  });

  // Debug VAD state changes
  useEffect(() => {
    console.log('VAD State:', {
      loading: vad.loading,
      listening: vad.listening,
      errored: vad.errored,
      userSpeaking: vad.userSpeaking
    });
  }, [vad.loading, vad.listening, vad.errored, vad.userSpeaking]);

  useEffect(() => {
    if (!availableVoices.some((voice) => voice.id === selectedVoice) && availableVoices[0]) {
      setSelectedVoice(availableVoices[0].id);
    }
  }, [availableVoices, selectedVoice]);

  useEffect(() => {
    localStorage.setItem('selectedVoice', selectedVoice);
  }, [selectedVoice]);

  // Create a new session on mount
  useEffect(() => {
    const createSession = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const response = await api.post('/session/create', { subject: 'General' });
        if (response.data && response.data.success) {
          setSessionId(response.data.data.sessionId);
        }
      } catch (err) {
        setSessionId(null);
      }
    };
    createSession();
  }, []);

  // Cleanup on unmount or leave
  useEffect(() => {
    return () => {
      // abort any ongoing fetch
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // push VAD stop state to backend
      if (sessionId) {
        const token = localStorage.getItem("token");
        if (token) {
          navigator.sendBeacon(
            `${process.env.REACT_APP_API_URL}/api/vad/stop`,
            JSON.stringify({ sessionId })
          );
        }
      }
      vad.pause();
    };
  }, [sessionId]);

  const handleCallEnd = () => {
    setMessages([]);
    if (vad) vad.pause();

    if (botAudioRef.current) {
      botAudioRef.current.pause();
      botAudioRef.current.src = "";
      botAudioRef.current = null;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    console.log("✅ Call ended: all processes stopped and API calls aborted");
    // navigate(-1)
  };

  useEffect(() => {
    const handleUnload = () => handleCallEnd();
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      handleCallEnd();
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  const handleSendAudioMessage = async (audioBlob: Blob, audioFile: File, duration: number) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('No token found. Please login.');
      return;
    }
    vad.pause();
    setIsResponding(true);

    abortControllerRef.current = new AbortController();

    try {
      const formData = new FormData();
      formData.append('audio', audioFile);
      formData.append('audioFlag', 'true');
      formData.append('subject', 'General');
      formData.append('sessionId', sessionId || '');

      // Add loading message
      setMessages(messages => [
        ...messages,
        {
          role: 'assistant',
          content: '',
          isUser: false,
          timestamp: new Date()
        }
      ]);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/llm/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response reader available');

      let accumulatedContent = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = new TextDecoder().decode(value);
        accumulatedContent += chunk;
      }
      // Show loading indicator until TTS is ready
      setMessages(messages => {
        const newMessages = [...messages];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && !lastMessage.isUser) {
          lastMessage.content = '';
        }
        return newMessages;
      });

      // TTS for bot response
      try {
        const plainText = stripMarkdown(accumulatedContent);
        const ttsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/llm/generateTTS`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ text: plainText, voice: selectedVoice }),
          signal: abortControllerRef.current.signal
        });
        if (ttsResponse.ok) {
          const arrayBuffer = await ttsResponse.arrayBuffer();
          const botAudioBlob = new Blob([arrayBuffer], { type: 'audio/wav' });
          const botAudioUrl = URL.createObjectURL(botAudioBlob);
          if (botAudioRef.current) botAudioRef.current.pause();
          botAudioRef.current = new Audio(botAudioUrl);
          botAudioRef.current.onloadedmetadata = () => {
            const totalDuration = botAudioRef.current!.duration; // In seconds
            const totalChars = plainText.length;
            const interval = (totalDuration * 1000) / totalChars; // ms per character
            let displayedText = '';
            let index = 0;
            setIsAudioPlaying(true);
            setPlayingAudioUrl(botAudioUrl);
            botAudioRef.current!.play();
            const streamInterval = setInterval(() => {
              if (index >= plainText.length) {
                clearInterval(streamInterval);
                setIsAudioPlaying(false);
                setPlayingAudioUrl(null);
                return;
              }
              displayedText += plainText[index];
              index++;
              setMessages(messages => {
                const newMessages = [...messages];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage && !lastMessage.isUser) {
                  lastMessage.content = displayedText;
                }
                return newMessages;
              });
            }, Math.max(interval, 10)); // Avoid too fast
            botAudioRef.current!.onended = () => {
              setIsAudioPlaying(false);
              setPlayingAudioUrl(null);
              clearInterval(streamInterval);
              setMessages(messages => {
                const newMessages = [...messages];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage && !lastMessage.isUser) {
                  lastMessage.content = plainText;
                }
                return newMessages;
              });
              // Reactivate VAD for next question
              setTimeout(() => {
                vad.start();
              }, 500); // Small delay to ensure cleanup is complete
            };
          };
        }
      } catch (err) {
        // TTS error, just show text
      }
    } catch (error) {
      if ((error as any).name === "AbortError") {
        console.log("Fetch aborted");
        return;
      }
      setMessages(messages => [
        ...messages.filter(msg => msg.content !== ''),
        {
          role: 'assistant',
          content: 'Error: Failed to get response. Please try again.',
          isUser: false,
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsResponding(false);
    }
  };

  // Play/pause audio (for bot and user messages)
  const handlePlayAudio = (audioUrl: string) => {
    if (playingAudioUrl === audioUrl && isAudioPlaying) {
      audioRef.current?.pause();
      setIsAudioPlaying(false);
      setPlayingAudioUrl(null);
    } else {
      if (audioRef.current) audioRef.current.pause();
      setPlayingAudioUrl(audioUrl);
      setIsAudioPlaying(true);
      setTimeout(() => {
        audioRef.current?.play();
      }, 100);
    }
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.onended = () => {
        setIsAudioPlaying(false);
        setPlayingAudioUrl(null);
      };
    }
  }, [playingAudioUrl]);

  useEffect(() => {
    document.body.classList.toggle('dark-mode', isDarkMode);
  }, [isDarkMode]);

  // Scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Stop VAD completely (pause and reset)
  const stopVADCompletely = () => {
    vad.pause();
    // Optionally clear messages or reset state if needed
  };

  return (
    <div className={`chat-container ${isDarkMode ? 'dark' : 'light'}`}> 
      <main className="chat-content vad-content">
        <header className="chat-header">
          <div className="header-content vad-header-content">
            <div className="vad-header-left">
              <span className="vad-header-pill">Zaptick Realtime system Beta</span>
              <div className="vad-control-group">
                <button
                  className={`vad-control-btn ${vad.listening ? 'is-listening' : ''} ${vad.loading ? 'is-loading' : ''}`}
                  onClick={vad.toggle}
                  disabled={vad.loading}
                >
                  {vad.loading ? (
                    <>
                      <span className="vad-loader" />
                      Loading...
                    </>
                  ) : vad.listening ? (
                    <>
                      <FiPause className="vad-control-icon" />
                      Pause
                    </>
                  ) : (
                    <>
                      <FiPlay className="vad-control-icon" />
                      Start
                    </>
                  )}
                </button>
                <button
                  className="vad-end-btn"
                  onClick={() => {
                    handleCallEnd();
                    navigate(-1);
                  }}
                >
                  <FiPhoneOff className="vad-control-icon" />
                  End Call
                </button>
                <button
                  className="vad-stop-btn"
                  onClick={stopVADCompletely}
                  disabled={vad.loading}
                >
                  <FiSquare className="vad-control-icon" />
                  Stop
                </button>
                {vad.errored && (
                  <button
                    className="vad-retry-btn"
                    onClick={() => {
                      console.log('Retrying VAD...');
                      vad.start();
                    }}
                  >
                    <FiPlay className="vad-control-icon" />
                    Retry
                  </button>
                )}
              </div>
            </div>

            <div className="header-actions">
              <div className="voice-selector-container" style={{ position: 'relative' }}>
                <button
                  className="voice-toggle-btn"
                  onClick={() => setIsVoiceMenuOpen((open) => !open)}
                  aria-label="Select voice"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    background: isDarkMode ? '#374151' : '#f3f4f6',
                    border: '1px solid',
                    borderColor: isDarkMode ? '#4b5563' : '#d1d5db',
                    borderRadius: 8,
                    color: isDarkMode ? '#e5e7eb' : '#1f2937',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <FiVolume2 size={18} />
                  <span>{currentVoice?.name || 'Luna'}</span>
                  <FiChevronDown size={14} />
                </button>

                {isVoiceMenuOpen && (
                  <>
                    <div
                      className="voice-menu-overlay"
                      onClick={() => setIsVoiceMenuOpen(false)}
                      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }}
                    />
                    <div
                      className="voice-menu"
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        right: 0,
                        background: isDarkMode ? '#1f2937' : '#ffffff',
                        border: '1px solid',
                        borderColor: isDarkMode ? '#374151' : '#e2e8f0',
                        borderRadius: 12,
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                        minWidth: 300,
                        maxWidth: '90vw',
                        maxHeight: 420,
                        overflowY: 'auto',
                        zIndex: 999,
                        padding: 8,
                      }}
                    >
                      <div
                        style={{
                          padding: '12px 16px',
                          borderBottom: '1px solid',
                          borderColor: isDarkMode ? '#374151' : '#e2e8f0',
                          marginBottom: 8,
                        }}
                      >
                        <h3
                          style={{
                            margin: 0,
                            fontSize: 14,
                            fontWeight: 600,
                            color: isDarkMode ? '#e5e7eb' : '#1f2937',
                          }}
                        >
                          Select Voice
                        </h3>
                      </div>

                      {groupedVoices.map(({ gender, voices }) => {
                        if (voices.length === 0) return null;
                        return (
                          <div key={gender} style={{ marginBottom: 12 }}>
                            <div
                              style={{
                                padding: '8px 16px',
                                fontSize: 12,
                                fontWeight: 600,
                                color: isDarkMode ? '#9ca3af' : '#6b7280',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                              }}
                            >
                              {gender}
                            </div>
                            {voices.map((voice) => (
                              <button
                                key={voice.id}
                                onClick={() => {
                                  setSelectedVoice(voice.id);
                                  setIsVoiceMenuOpen(false);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  textAlign: 'left',
                                  background: selectedVoice === voice.id ? '#3b82f6' : 'transparent',
                                  color: selectedVoice === voice.id ? '#ffffff' : isDarkMode ? '#e5e7eb' : '#1f2937',
                                  border: 'none',
                                  borderRadius: 8,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  fontSize: 14,
                                  transition: 'all 0.2s ease',
                                  marginBottom: 4,
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: 500 }}>{voice.name}</div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      opacity: 0.8,
                                      marginTop: 2,
                                    }}
                                  >
                                    {voice.accent} | {voice.gender}
                                  </div>
                                </div>
                                {selectedVoice === voice.id && <FiCheck size={18} />}
                              </button>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              <button
                className="theme-toggle"
                onClick={() => setIsDarkMode((d) => !d)}
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? <FiSun /> : <FiMoon />}
              </button>
            </div>
          </div>
        </header>
        <div className="messages-container">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`message ${msg.isUser ? 'user-message' : 'ai-message'}`}
            >
              <div className="message-content-wrapper">
                {/* Only show play/stop button for user audio messages */}
                {msg.audio && msg.isUser && (
                  <div className="message-audio">
                    <button
                      className="audio-play-button"
                      onClick={() => handlePlayAudio(msg.audio!.audioUrl)}
                    >
                      {playingAudioUrl === msg.audio.audioUrl && isAudioPlaying ? <FiPause /> : <FiPlay />}
                    </button>
                    {playingAudioUrl === msg.audio.audioUrl && (
                      <audio ref={audioRef} src={msg.audio.audioUrl} style={{ display: 'none' }} />
                    )}
                  </div>
                )}
                {!msg.isUser && (
                  <RichText className="message-text" text={msg.content} />
                )}
                <span className="timestamp">
                  {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          ))}
          {isResponding && (
            <div className="ai-message">
              <div className="message-content-wrapper">
                <span className="message-text message-pending">Bot is thinking...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        <div className="chat-input-container vad-status-container">
          {/* VAD Status Indicator */}
          <div className="vad-status-indicator">
            {isResponding ? (
              <>
                <span className="vad-status-dot is-busy" />
                <span>Processing... VAD paused</span>
              </>
            ) : vad.errored ? (
              <>
                <span className="vad-status-dot is-error" />
                <span>VAD Error - Click Retry</span>
              </>
            ) : vad.listening ? (
              <>
                <span className={`vad-status-dot ${vad.userSpeaking ? 'is-speaking' : 'is-listening'}`} />
                <span>{vad.userSpeaking ? 'Speaking detected...' : 'Listening for voice...'}</span>
              </>
            ) : vad.loading ? (
              <>
                <span className="vad-status-dot is-loading" />
                <span>VAD loading...</span>
              </>
            ) : (
              <>
                <span className="vad-status-dot" />
                <span>VAD stopped</span>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Vad;
